import { Button } from "@components/controls/Button"
import { SearchBox } from "@components/controls/SearchBox"
import { BinaryToggle } from "@components/controls/Toggle"
import { useInfiniteAwardShowcase } from "@hooks/queries"
import { useSearchParams } from "eddev/routing"
import { twMerge } from "tailwind-merge"
import { tv } from "tailwind-variants"
import { AwardFiltersFragment, AwardStatusOption } from "../../../types.graphql"
import { useEffect, useMemo, useState } from "react"
import { useReduceMemo } from "@hooks/state-hooks"

type FilterValues = {
  year?: number
  student?: boolean
  category?: string
  types?: string[]
  q?: string
  studio?: number
}

type Props = {
  className?: string
  options: AwardFiltersFragment
  values: FilterValues
  onValueChange: (values: FilterValues) => void
}

const createStyles = tv({
  slots: {
    wrapper: "stack-y-5",
    section: "stack-y-2",
    label: "type-ui-s-roman-caps",
    buttons: "flex gap-1 flex-wrap",
  },
})

export function useAwardsSearch(args: { options: AwardFiltersFragment }) {
  const [params, filter] = useSearchParams<{
    year?: number
    student?: boolean
    category?: string
    types?: string[]
    studio?: number
    q?: string
  }>({
    student: false,
    studio: 0,
  })

  if (!params.year && !params.studio) {
    params.year = args.options?.years?.reduce((y, { year }) => Math.max(year ?? 0, y), 0)
  }

  const hasFilters = !!params.year || !!params.category || !!params.studio
  const lookupOrderType = params.studio && !params.category ? "year" : "awardType"
  const lookup = useInfiniteAwardShowcase(
    {
      student: params.student ? true : false,
      studio: params.studio ? Number(params.studio) : undefined,
      year: Number(params.year) ? Number(params.year) : undefined,
      types: params.types,
      orderType: lookupOrderType,
      categorySlug: params.category,
      limit: 60,
      search: params.q,
    },
    {
      enabled: hasFilters,
    }
  )

  const orderType = useReduceMemo(
    (last): typeof lookupOrderType => {
      if (lookup.isLoading) {
        return (last as typeof lookupOrderType) ?? lookupOrderType
      } else {
        return lookupOrderType
      }
    },
    [lookup.isLoading, lookupOrderType]
  )

  return {
    lookup,
    params,
    filter,
    hasFilters,
    orderType,
  }
}

export function AwardsFilter(props: Props) {
  const styles = createStyles()

  const awardTypeDisabled = false

  const year = props.options.years?.find((y) => y.year == props.values.year)
  const categories = year?.categories
    ?.filter((c) => {
      if (props.values.student) {
        return c?.type === "student"
      } else {
        return c?.type === "common" || c?.type === "nomination"
      }
    })
    .sort((a, b) => (a?.title ?? "").localeCompare(b?.title ?? ""))
    .sort((a, b) => (a?.type ?? "")?.localeCompare(b?.type ?? ""))

  const category = categories?.find((c) => c?.slug === props.values.category)
  const hasStudent = year?.categories?.some((c) => c?.type === "student")
  const awardTypes = props.options.types?.filter((t) => !category || category?.levels?.includes(t?.label!))

  useEffect(() => {
    let value = props.values
    if (props.values.student && !hasStudent) {
      value = { ...props.values, student: false }
    }
    if (props.values.category && !categories?.find((c) => c?.slug === props.values.category)) {
      value = { ...props.values, category: undefined }
    }
    if (value !== props.values) {
      props.onValueChange(value)
    }
  })

  return (
    <div className={twMerge(styles.wrapper(), props.className)}>
      {/* Year */}
      <div className={styles.section()}>
        <h4 className={styles.label()}>Year</h4>
        <div className={styles.buttons()}>
          {props.options.years?.map((year) => (
            <Button
              key={year.year}
              variant="outline"
              onClick={() => {
                if (props.values.year == year.year) {
                  props.onValueChange({ ...props.values, year: undefined })
                } else {
                  props.onValueChange({ ...props.values, year: year.year! })
                }
              }}
              active={props.values.year == year.year}
            >
              {year.year}
            </Button>
          ))}
        </div>
      </div>

      {/* Category */}
      <div className={styles.section()}>
        <h4 className={styles.label()}>Category</h4>
        <div className={styles.buttons()}>
          {categories?.map((cat) => (
            <Button
              key={cat?.title}
              variant="outline"
              onClick={() => {
                if (props.values.category == cat?.slug) {
                  props.onValueChange({ ...props.values, category: undefined })
                } else {
                  props.onValueChange({ ...props.values, category: cat?.slug! })
                }
              }}
              active={props.values.category == cat?.slug}
            >
              {cat?.title?.replace(/^Student\ /, "")}
            </Button>
          ))}
        </div>
      </div>

      {/* Award */}
      {year?.status === "winners" && awardTypes?.length! > 1 && (
        <div className={styles.section()}>
          <h4 className={styles.label()}>Award</h4>
          <div className={styles.buttons()}>
            {awardTypes?.map((type) => (
              <Button
                key={type?.label}
                variant="outline"
                disabled={awardTypeDisabled}
                onClick={() => {
                  if (awardTypeDisabled) return
                  if (props.values.types?.includes(type?.slug!)) {
                    props.onValueChange({ ...props.values, types: props.values.types?.filter((t) => t != type?.slug) })
                  } else {
                    props.onValueChange({ ...props.values, types: [...(props.values.types ?? []), type?.slug!] })
                  }
                }}
                active={awardTypeDisabled ? false : props.values.types?.includes(type?.slug!)}
              >
                {type?.label}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Professional/Student Toggle */}
      {hasStudent && (
        <div>
          <BinaryToggle
            left={{ label: "Professional", value: false }}
            right={{ label: "Student", value: true }}
            value={!!props.values.student}
            onToggle={(value) => {
              props.onValueChange({ ...props.values, student: value, studio: undefined })
            }}
          />
        </div>
      )}

      <div className="flex-auto" />

      <SearchBox
        defaultValue={props.values.q}
        onSearch={(term) => {
          props.onValueChange({
            ...props.values,
            studio: undefined,
            q: term,
          })
        }}
        placeholder="Search awards..."
      />
    </div>
  )
}

export function MobileAwardsFilter(props: Props & { onClose: () => void }) {
  const [values, setValues] = useState(props.values)

  useEffect(() => {
    setValues(props.values)
  }, [props.values])

  return (
    <div className="flex flex-col gap-7">
      <AwardsFilter
        {...props}
        className="stack-y-2"
        values={values}
        onValueChange={(values) => {
          setValues(values)
        }}
      />
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => {
            props.onClose()
            setValues({})
            props.onValueChange({})
          }}
        >
          Reset
        </Button>
        <Button
          onClick={() => {
            props.onValueChange(values)
            props.onClose()
          }}
        >
          Apply
        </Button>
      </div>
    </div>
  )
}
